/**
 * SupportPage - tickets de suporte do cliente
 */
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { LifeBuoy, Plus, Send, Loader2 } from "lucide-react";
import { api, getApiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import StatusBadge from "@/components/shared/StatusBadge";
import EmptyState from "@/components/shared/EmptyState";
import { fmtDateTime } from "@/lib/date";

export default function SupportPage() {
    const [tickets, setTickets] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ subject: "", message: "", priority: "media" });
    const [sel, setSel] = useState(null);
    const [reply, setReply] = useState("");
    const [saving, setSaving] = useState(false);

    const load = () => api.get("/contact/tickets").then((r) => setTickets(r.data));
    useEffect(() => { load(); }, []);

    const submit = async () => {
        if (!form.subject || !form.message) {
            toast.error("Preencha assunto e mensagem.");
            return;
        }
        setSaving(true);
        try {
            await api.post("/contact/ticket", form);
            toast.success("Ticket criado");
            setOpen(false);
            setForm({ subject: "", message: "", priority: "media" });
            load();
        } catch (e) {
            toast.error(getApiErrorMessage(e));
        } finally {
            setSaving(false);
        }
    };

    const sendReply = async () => {
        if (!reply || !sel) return;
        try {
            await api.post(`/contact/tickets/${sel.id}/reply`, { message: reply });
            toast.success("Resposta enviada");
            setReply("");
            const fresh = await api.get("/contact/tickets");
            setTickets(fresh.data);
            setSel(fresh.data.find((t) => t.id === sel.id));
        } catch (e) {
            toast.error(getApiErrorMessage(e));
        }
    };

    return (
        <div className="space-y-6" data-testid="support-page">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Suporte</h1>
                    <p className="text-muted-foreground">Estamos aqui para ajudar.</p>
                </div>
                <Button onClick={() => setOpen(true)} className="btn-grad-purple text-white" data-testid="support-new-ticket-button">
                    <Plus className="h-4 w-4 mr-2" /> Novo pedido
                </Button>
            </div>

            {tickets.length === 0 ? (
                <EmptyState
                    icon={LifeBuoy}
                    title="Sem pedidos de suporte"
                    description="Tem alguma dúvida? Crie um pedido e a nossa equipa responderá o mais rápido possível."
                    ctaLabel="Novo pedido"
                    onCta={() => setOpen(true)}
                    testid="support-empty"
                />
            ) : (
                <div className="space-y-3">
                    {tickets.map((t) => (
                        <Card
                            key={t.id}
                            className="glass-card p-4 rounded-2xl border-white/10 cursor-pointer hover:border-[#6B21D9]/40"
                            onClick={() => setSel(t)}
                            data-testid={`support-ticket-${t.id}`}
                        >
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div className="flex items-center gap-2">
                                    <StatusBadge status={t.status} />
                                    <h3 className="font-medium">{t.subject}</h3>
                                </div>
                                <span className="text-xs text-muted-foreground">{fmtDateTime(t.created_at)}</span>
                            </div>
                            <p className="mt-2 text-sm text-muted-foreground line-clamp-1">{t.message}</p>
                        </Card>
                    ))}
                </div>
            )}

            {/* New Ticket Dialog */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Novo pedido de suporte</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Assunto</Label>
                            <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="support-subject-input" />
                        </div>
                        <div>
                            <Label>Prioridade</Label>
                            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="baixa">Baixa</SelectItem>
                                    <SelectItem value="media">Média</SelectItem>
                                    <SelectItem value="alta">Alta</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Mensagem</Label>
                            <Textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="support-message-input" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button onClick={submit} disabled={saving} className="btn-grad-purple text-white" data-testid="support-submit-button">
                            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                            Enviar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Detail Dialog */}
            <Dialog open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {sel && <StatusBadge status={sel.status} />}
                            <span>{sel?.subject}</span>
                        </DialogTitle>
                    </DialogHeader>
                    {sel && (
                        <div className="space-y-3">
                            <Card className="p-3 rounded-xl bg-secondary/40">
                                <p className="text-sm whitespace-pre-line">{sel.message}</p>
                                <div className="text-xs text-muted-foreground mt-2">{fmtDateTime(sel.created_at)}</div>
                            </Card>
                            {(sel.replies || []).map((r, i) => (
                                <Card
                                    key={i}
                                    className={`p-3 rounded-xl ${r.from === "admin" ? "bg-[#6B21D9]/10 border-[#6B21D9]/30" : "bg-secondary/40"}`}
                                >
                                    <p className="text-sm whitespace-pre-line">{r.message}</p>
                                    <div className="text-xs text-muted-foreground mt-2">
                                        {r.from === "admin" ? "Equipa MeSafa" : sel.user_name || "Você"} · {fmtDateTime(r.at)}
                                    </div>
                                </Card>
                            ))}
                            {sel.status !== "resolvido" && (
                                <div className="space-y-2">
                                    <Textarea placeholder="Escreva resposta..." rows={3} value={reply} onChange={(e) => setReply(e.target.value)} />
                                    <Button onClick={sendReply} className="btn-grad-purple text-white">
                                        <Send className="h-4 w-4 mr-2" /> Enviar resposta
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
